<script lang="ts">
    import tabIcon from "@/assets/gear.svg";
    import ListLeverantor from "./ListLeverantor.vue";

    export default {
        name: "ListMaterial",
        tabName: "Material",
        icon: tabIcon,
    };
</script>

<script setup lang="ts">
    import CollapsibleSection from "@/components/generic/CollapsibleSection.vue";
    import MaterialForm from "@/tabs/Admin/forms/MaterialForm.vue";
    import ListMaterialgrupp from "@/tabs/Admin/ListMaterialgrupp/ListMaterialgrupp.vue";
    import { ref, onBeforeUnmount, onBeforeMount, computed, toRefs } from "vue";
    import { openDialog } from "@/utils";
    import AdminLockBorder from "@/components/AdminLockBorder.vue";
    import KlinikenTable from "@/components/Table/KlinikenTable.vue";
    import CustomButton from "@/components/Button.vue";
    import { useStore } from "@/store";
    import MaterialFilter from "@/tabs/Admin/MaterialFilter.vue";

    // TODO Ändra till användning av type i materialModule i Vue3
    interface Material {
        id: string;
        artikelnummer: string;
        namn: string;
        leverantor: string;
        streckkod1: string;
        streckkod2: string;
        lokalt_artikelnummer: string;
    }

    const store = useStore();

    const props = defineProps<{
        tabId: number;
    }>();

    const columns = [
        {
            title: "Namn",
            name: "namn",
            sort: "namn",
        },
        {
            title: "Artikelnummer",
            name: "artikelnummer",
            sort: "artikelnummer",
        },
        {
            title: "Streckkod 1",
            name: "streckkod1",
            sort: "streckkod1",
        },
        {
            title: "Streckkod 2",
            name: "streckkod2",
            sort: "streckkod2",
        },
        {
            title: "Lokalt artikelnummer",
            name: "lokalt_artikelnummer",
            sort: "lokalt_artikelnummer",
        },
        {
            title: "Leverantör",
            name: "leverantor",
            getContent: {
                compute: (item: { leverantor: string }) => {
                    const leverantor = store.getters["leverantor/leverantorById"]({ id: item.leverantor });

                    return leverantor?.namn ?? "";
                },
            },
        },
    ];
    const dropdownActions: {
        name: string;
        title: string;
        show: () => boolean;
    }[] = [
        {
            name: "editMaterial",
            title: "Redigera material",
            show: () => true,
        },
    ];
    const listPage = ref<number>(1);
    const listLimit = ref<number>(20);
    const filterValues = ref({});
    const updatedFilterValues = ref({});

    const handleSearch = (e: Record<string, any>) => {
        filterParams.value = Object.values(e).length === 0 ? {} : { ...updatedFilterValues.value };
        fetchMaterial({});
        return;
    };
    const handleFilterValuesUpdated = (data: Record<string, any>) => {
        updatedFilterValues.value = data;
        return;
    };
    const filterParams = ref<
        | {
              [key: string]: string;
          }
        | undefined
    >();
    const subsetName = ref<string>("default");
    const listCount = computed(() => store.state.material.subsetTotals?.[subsetName.value] ?? 0);
    const material = computed<Material[]>(() =>
        store.getters["material/materialSubset"]({
            subsetName: subsetName.value,
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        })
    );
    const nyaMaterial = computed<Material[]>(() =>
        store.getters["material/materialSubset"]({
            subsetName: "newlyCreated",
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        })
    );
    const redigeradeMaterial = computed<Material[]>(() =>
        store.getters["material/materialSubset"]({
            subsetName: "newlyUpdated",
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        })
    );

    const { tabId } = toRefs(props);

    defineExpose({
        tabId,
    });

    const onChangeLimit = async (newLimit: number) => {
        listLimit.value = newLimit;

        await fetchMaterial({
            limit: newLimit,
            page: 1,
        });
    };

    const onLoadPage = async (page: number) => {
        await fetchMaterial({ page });
    };

    const openCreateMaterial = () => {
        store.dispatch("tabs/openTab", {
            component: MaterialForm,
        });
    };

    const openEditMaterial = (material: Material) => {
        store.dispatch("tabs/openTab", {
            component: MaterialForm,
            parameters: {
                materialId: material.id,
            },
        });
    };

    const onSortChange = async (newSortBy: string) => {
        filterParams.value = {
            ...(filterParams.value ? filterParams.value : {}),
            ordering: newSortBy,
        };

        await fetchMaterial({
            page: 1,
        });
    };

    const fetchMaterial = async ({ page, limit = listLimit.value }: { page?: number; limit?: number }) => {
        try {
            const {
                nextPage,
                total,
                subsetName: fetchedSubsetName,
            } = await store.dispatch("material/fetchMaterial", {
                page,
                limit,
                filterParams: filterParams.value,
            });

            listPage.value = page || 1;
            subsetName.value = fetchedSubsetName;
        } catch (_) {
            console.log(_);
            openDialog("Kunde inte hämta material", "error");
        }
    };

    const openGrupper = () => {
        store.dispatch("tabs/openTab", {
            component: ListMaterialgrupp,
        });
    };

    const openLeverantorer = () => {
        store.dispatch("tabs/openTab", {
            component: ListLeverantor,
        });
    };

    onBeforeMount(async () => {
        await fetchMaterial({ page: listPage.value });
        await store.dispatch("material/addSubscriber");
        await store.dispatch("leverantor/addSubscriber");
        await store.dispatch("materialgrupp/addSubscriber");
    });

    onBeforeUnmount(async () => {
        await store.dispatch("material/removeSubscriber");
        await store.dispatch("leverantor/removeSubscriber");
        await store.dispatch("materialgrupp/removeSubscriber");
    });
</script>

<template>
    <div class="list-material">
        <admin-lock-border />
        <material-filter v-model="filterValues" @input="handleSearch" @filterUpdated="handleFilterValuesUpdated" />
        <div class="header">
            <h1>Material</h1>
            <CustomButton type="button" variant="hollow" @click="openCreateMaterial" :style="{ padding: 0 }">
                <img src="@/assets/plus_circle_blue.svg" />
                <span>Skapa material</span>
            </CustomButton>
            <CustomButton type="button" variant="link" @click="openGrupper">
                <span>Materialgrupper</span>
            </CustomButton>
            <CustomButton type="button" variant="link" @click="openLeverantorer">
                <span>Leverantörer</span>
            </CustomButton>
        </div>
        <div class="newly" v-if="redigeradeMaterial.length > 0">
            <collapsible-section isSingle :isExpanded="false" heading="Nyligen redigerade material">
                <kliniken-table
                    :items="redigeradeMaterial"
                    :columns="columns"
                    @editMaterial="openEditMaterial"
                    @createMaterial="openCreateMaterial"
                    :has-dropdown="true"
                    :dropdown-actions="dropdownActions"
                />
            </collapsible-section>
        </div>
        <div class="newly-created" v-if="nyaMaterial.length > 0">
            <collapsible-section isSingle :isExpanded="false" heading="Nyligen tillagda material">
                <kliniken-table
                    :items="nyaMaterial"
                    :columns="columns"
                    @editMaterial="openEditMaterial"
                    @createMaterial="openCreateMaterial"
                    :has-dropdown="true"
                    :dropdown-actions="dropdownActions"
                />
            </collapsible-section>
        </div>
        <div>
            <h3 v-if="nyaMaterial.length > 0 || redigeradeMaterial.length > 0">Alla material</h3>
            <kliniken-table
                :items="material"
                :columns="columns"
                :count="listCount"
                :currentPage="listPage"
                :limit="listLimit"
                paginationStyle="paging"
                @sort="onSortChange"
                @loadPage="onLoadPage"
                @changeLimit="onChangeLimit"
                @editMaterial="openEditMaterial"
                @createMaterial="openCreateMaterial"
                :showBottom="true"
                :showLimitDropdown="true"
                :has-dropdown="true"
                :dropdown-actions="dropdownActions"
            />
        </div>
    </div>
</template>

<style lang="scss">
    .list-material {
        .header {
            h1 {
                margin: 0;
            }

            display: flex;
            gap: 20px;
            align-items: center;
        }

        .newly {
            margin-top: 1rem;
        }
    }
</style>
